// Éléments communs des écrans du lot 3 (charte D-038, « exceptions d'abord ») : étiquettes d'état d'un ordre,
// âge d'une position, exception d'une mission, liens vers les bons PDF générés par le serveur, accès aux
// données de terrain (en ligne par l'API, hors connexion par le paquet de travail chiffré).

import { useQuery } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { deProduit, LIBELLES_ETATS_BESOIN, LIBELLES_TYPES_ETAPE, LIBELLES_URGENCES, type EtatBesoin, type TypeEtape, type UrgenceBesoin } from '@carburanttrack/shared/logistique';
import { dateHeure } from '../../composants/format';
import { useHorsLigne } from '../../horsligne/contexte';
import { quantite } from '../stocks/composants';
import type { LigneOrdre, PaquetTerrain } from './types';
import './logistique.css';

export { quantite };

/** Ton d'exception d'une mission (retard, arrêt prolongé, position ancienne ou inconnue en route). */
export function exceptionMission(l: Pick<LigneOrdre, 'etat' | 'retardHeures' | 'arretHeures' | 'position' | 'vehicule'>): { ton: 'rouge' | 'ambre'; libelle: string } | null {
  if (l.retardHeures !== null && l.retardHeures > 0) return { ton: 'rouge', libelle: `En retard de ${formaterNombre(String(l.retardHeures), 1)} h` };
  if (l.arretHeures !== null) return { ton: 'ambre', libelle: `Sans étape depuis ${formaterNombre(String(l.arretHeures), 1)} h` };
  if (l.etat === 'en_route' && l.position && l.position.statut !== 'recente') return { ton: 'ambre', libelle: l.position.statut === 'inconnue' ? 'Position inconnue' : 'Position ancienne' };
  if (l.etat === 'emis' && !l.vehicule) return { ton: 'ambre', libelle: 'Véhicule à affecter' };
  return null;
}

export function EtiquetteEtatOrdre({ etat, libelle }: { etat: string; libelle: string }) {
  const ton = etat === 'en_rapprochement' ? 'ambre' : etat === 'annule' ? 'gris' : etat === 'recu' || etat === 'solde' ? 'vert' : '';
  return <span className={`etiquette ${ton}`}>{libelle}</span>;
}

export function EtiquetteException({ exception }: { exception: { ton: 'rouge' | 'ambre'; libelle: string } | null }) {
  if (!exception) return null;
  return <span className={`etiquette ${exception.ton}`}>{exception.libelle}</span>;
}

/** Âge de la dernière position : toujours affiché avec son libellé, jamais par la seule couleur (R-27). */
export function AgePosition({ ligne }: { ligne: Pick<LigneOrdre, 'position' | 'motifPositionMasquee' | 'etat'> }) {
  if (!ligne.position) return <span className="attenue">{ligne.motifPositionMasquee ?? 'position non lisible'}</span>;
  if (ligne.etat === 'emis') return <span className="attenue">pas encore chargé</span>;
  const p = ligne.position;
  const source = p.source === 'appareil' ? 'appareil' : p.source === 'declaree' ? 'déclarée' : null;
  return (
    <span className={p.statut === 'recente' ? '' : 'etiquette ambre'}>
      {p.libelle}
      {source && <small className="attenue"> ({source})</small>}
    </span>
  );
}

export const libelleEtatBesoin = (e: string) => LIBELLES_ETATS_BESOIN[e as EtatBesoin] ?? e;
export const libelleUrgence = (u: string) => LIBELLES_URGENCES[u as UrgenceBesoin] ?? u;
export const libelleEtape = (t: string) => LIBELLES_TYPES_ETAPE[t as TypeEtape] ?? t;

export function LiensBons({ id, numero, bonChargement, bonLivraison }: { id: string; numero: string; bonChargement: boolean; bonLivraison: boolean }) {
  if (!bonChargement && !bonLivraison) return <span className="attenue">Bons établis après le chargement</span>;
  return (
    <span className="actions">
      {bonChargement && (
        <a className="bouton" href={`/api/v1/ordres-transport/${id}/bon-chargement.pdf`} target="_blank" rel="noreferrer">
          Bon de chargement {numero} (PDF)
        </a>
      )}
      {bonLivraison && (
        <a className="bouton" href={`/api/v1/ordres-transport/${id}/bon-livraison.pdf`} target="_blank" rel="noreferrer">
          Bon de livraison (PDF)
        </a>
      )}
    </span>
  );
}

export const volumeOrdre = (l: Pick<LigneOrdre, 'quantiteChargee' | 'volumePrevu' | 'unite'>) => quantite(l.quantiteChargee ?? l.volumePrevu, l.unite);

/** Résumé d'une mission en une ligne : volume, produit, origine et destination. */
export const resumeOrdre = (l: LigneOrdre) => `${volumeOrdre(l)} ${deProduit(l.produitCode)}, ${l.origine.nom} vers ${l.destination.nom}`;

export const arrivee = (l: LigneOrdre) => (l.dateArrivee ? `arrivée ${dateHeure(l.dateArrivee)}` : `arrivée prévue ${dateHeure(l.dateArriveePrevue)}`);

/**
 * Paquet de travail de l'utilisateur : actualisé à chaque ouverture en ligne, lu sur l'appareil hors connexion
 * (« consultation des dernières données synchronisées »).
 */
export function usePaquet() {
  const { synchroniseur, etat } = useHorsLigne();
  return useQuery({
    queryKey: ['paquet-terrain', etat.paquetVersion, etat.derniereSynchro],
    queryFn: async () => {
      if (etat.enLigne) {
        try {
          await synchroniseur.actualiserPaquet();
        } catch {
          // Serveur injoignable : la dernière copie locale est utilisée.
        }
      }
      const p = await synchroniseur.paquet<PaquetTerrain>();
      return p ? { ...p, contenu: p.contenu } : null;
    },
    retry: false,
    staleTime: 30_000,
  });
}

/** Horodatage local de l'événement (champ datetime-local) vers Date. */
export function dateLocale(valeur: string): Date {
  return new Date(`${valeur}:00+01:00`);
}

/** Position de l'appareil si l'utilisateur l'autorise ; sinon null (la position déclarée reste possible). */
export function positionAppareil(delaiMs = 8000): Promise<{ longitude: number; latitude: number; precisionMetres: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ longitude: p.coords.longitude, latitude: p.coords.latitude, precisionMetres: Math.round(p.coords.accuracy) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: delaiMs, maximumAge: 60_000 },
    );
  });
}

/** Bouton « afficher plus » d'une liste longue : n'apparaît que s'il reste des lignes à montrer. */
export function AfficherPlus({ affiches, total, pas, libelle, onPlus }: { affiches: number; total: number; pas: number; libelle: string; onPlus: () => void }) {
  if (total <= affiches) return null;
  return (
    <div className="actions">
      <button type="button" className="bouton" onClick={onPlus}>
        Afficher {formaterNombre(Math.min(pas, total - affiches))} {libelle} de plus ({formaterNombre(affiches)} sur {formaterNombre(total)} affichés)
      </button>
    </div>
  );
}
